import React, { useState, useEffect } from 'react'
import {
  Box,
  Paper,
  Typography,
  Switch,
  FormControlLabel,
  FormGroup,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material'
import { supabase } from '../../config/supabase'

const brand = {
  violet: '#5c4b99',
  navy: '#2d2855',
}

export default function Settings() {
  const [performanceVisible, setPerformanceVisible] = useState(true)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  // Load settings on mount
  useEffect(() => {
    loadSettings()
  }, [])

  const loadSettings = async () => {
    try {
      setLoading(true)
      setError(null)

      // Check if settings table exists and get the performance visibility setting
      const { data, error: fetchError } = await supabase
        .from('app_settings')
        .select('value')
        .eq('key', 'performance_visible')
        .single()

      if (fetchError) {
        // If table doesn't exist or no row found, default to true (visible)
        if (fetchError.code === 'PGRST116') {
          // No rows returned - create default setting
          await createDefaultSetting()
        } else {
          console.error('Error loading settings:', fetchError)
          // Continue with default value
        }
      } else if (data) {
        setPerformanceVisible(data.value === true || data.value === 'true')
      }
    } catch (err) {
      console.error('Error in loadSettings:', err)
      setError('Kunne ikke indlæse indstillinger')
    } finally {
      setLoading(false)
    }
  }

  const createDefaultSetting = async () => {
    try {
      await supabase
        .from('app_settings')
        .insert({
          key: 'performance_visible',
          value: true,
          description: 'Controls whether the Performance feature is visible to all users',
        })
      setPerformanceVisible(true)
    } catch (err) {
      console.error('Error creating default setting:', err)
    }
  }

  const handleTogglePerformance = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.checked
    setPerformanceVisible(newValue)
    setSaving(true)
    setError(null)
    setSuccessMessage(null)

    try {
      // Upsert the setting
      const { error: upsertError } = await supabase
        .from('app_settings')
        .upsert(
          {
            key: 'performance_visible',
            value: newValue,
            description: 'Controls whether the Performance feature is visible to all users',
            updated_at: new Date().toISOString(),
          },
          {
            onConflict: 'key',
          }
        )

      if (upsertError) {
        throw upsertError
      }

      setSuccessMessage(
        newValue
          ? 'Performance-funktionen er nu synlig for alle brugere'
          : 'Performance-funktionen er nu skjult for alle brugere'
      )

      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage(null)
      }, 3000)
    } catch (err: any) {
      console.error('Error saving setting:', err)
      setError(err.message || 'Kunne ikke gemme indstilling')
      // Revert the toggle
      setPerformanceVisible(!newValue)
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 1, fontWeight: 600, color: brand.navy }}>
        Indstillinger
      </Typography>
      <Typography variant="body2" sx={{ mb: 3, color: '#666' }}>
        Administrer systemindstillinger og funktioner
      </Typography>

      <Paper sx={{ p: 3, maxWidth: 800 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: brand.navy }}>
          Funktionssynlighed
        </Typography>
        <Typography variant="body2" sx={{ mb: 3, color: '#666' }}>
          Kontrollér hvilke funktioner der er synlige for alle brugere. Som administrator kan du
          altid se alle funktioner.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {successMessage && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {successMessage}
          </Alert>
        )}

        <Divider sx={{ my: 2 }} />

        <FormGroup>
          <FormControlLabel
            control={
              <Switch
                checked={performanceVisible}
                onChange={handleTogglePerformance}
                disabled={saving}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: brand.violet,
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: brand.violet,
                  },
                }}
              />
            }
            label={
              <Box>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  Performance Dashboard
                </Typography>
                <Typography variant="body2" sx={{ color: '#666', fontSize: '0.875rem' }}>
                  Når deaktiveret, vil Performance-funktionen være skjult for alle brugere (undtagen
                  administratorer)
                </Typography>
              </Box>
            }
          />
        </FormGroup>

        {saving && (
          <Box display="flex" alignItems="center" mt={2}>
            <CircularProgress size={16} sx={{ mr: 1 }} />
            <Typography variant="body2" color="text.secondary">
              Gemmer...
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  )
}
